import React, { useState, useEffect, useRef } from 'react';
import { 
  Sparkles, Upload, Play, Trash2, BookOpen, Headphones, FileText, Check, AlertCircle, Loader2, RefreshCw,
  CheckCircle2, AlertTriangle, Activity, FileCode, Gauge, ListFilter, Sliders, Info, ChevronDown, ChevronUp, Layers
} from 'lucide-react';
import { 
  testAlignStorage, 
  SavedTestAlignSession, 
  SavedTestAlignMeta, 
  TaleFyiNativeAlignment 
} from '../services/testAlignStorage';
import { 
  TaleAlignDiagnosticService, 
  TaleDomItem, 
  TaleDiagnosticReport 
} from '../services/taleAlignDiagnosticService';
import { TestAlignReaderPlayer } from './TestAlignReaderPlayer';

export const TestAlignSection: React.FC = () => {
  const [sessions, setSessions] = useState<SavedTestAlignMeta[]>([]);
  const [activeSession, setActiveSession] = useState<SavedTestAlignSession | null>(null);
  const [isLoadingList, setIsLoadingList] = useState(false);

  // Upload Form State
  const [alignmentFile, setAlignmentFile] = useState<File | null>(null);
  const [alignmentData, setAlignmentData] = useState<TaleFyiNativeAlignment | null>(null);

  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioUrlInput, setAudioUrlInput] = useState<string>('');

  const [bookHtmlFile, setBookHtmlFile] = useState<File | null>(null);
  const [bookHtmlContent, setBookHtmlContent] = useState<string>('');
  const [domItems, setDomItems] = useState<TaleDomItem[] | null>(null);

  // Diagnostic State
  const [diagnosticReport, setDiagnosticReport] = useState<TaleDiagnosticReport | null>(null);
  const [selectedOffset, setSelectedOffset] = useState<number>(2);
  const [activeSampleTab, setActiveSampleTab] = useState<'beginning' | 'middle' | 'end'>('beginning');
  const [isDiagnosticOpen, setIsDiagnosticOpen] = useState(true);

  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [language, setLanguage] = useState('pt');

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const alignmentInputRef = useRef<HTMLInputElement | null>(null);
  const audioInputRef = useRef<HTMLInputElement | null>(null);
  const bookHtmlInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    loadSavedSessions();
  }, []);

  const loadSavedSessions = async () => {
    setIsLoadingList(true);
    try {
      const list = await testAlignStorage.getAllSessions();
      setSessions(list);
    } catch (err) {
      console.warn('Erro ao carregar sessões de alinhamento:', err);
    } finally {
      setIsLoadingList(false);
    }
  };

  // Executa diagnóstico assim que ambos os arquivos estiverem disponíveis
  const triggerDiagnosisIfReady = (alignment: TaleFyiNativeAlignment | null, items: TaleDomItem[] | null) => {
    if (alignment && items && items.length > 0) {
      try {
        const report = TaleAlignDiagnosticService.runDiagnosis(alignment, items);
        setDiagnosticReport(report);
        const recOffset = report.segmentationAnalysis.recommendedOffset;
        setSelectedOffset(recOffset);

        // Prepara o HTML formatado com o offset determinado
        const preparedHtml = TaleAlignDiagnosticService.generatePreparedHtml(alignment, items, recOffset);
        setBookHtmlContent(preparedHtml);
      } catch (err: any) {
        console.warn('[TestAlign] Erro ao executar diagnóstico:', err);
      }
    }
  };

  // Handler: Alinhamento Nativo JSON / TXT
  const handleAlignmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMessage(null);
    try {
      const text = await file.text();
      let parsed: any;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error('O arquivo de alinhamento selecionado não contém um JSON válido.');
      }

      const validated = TaleAlignDiagnosticService.validateAlignment(parsed);
      if (!validated.isValid || !validated.alignment) {
        throw new Error(validated.error || 'Estrutura inválida no arquivo de alinhamento.');
      }

      const alignmentObj = validated.alignment;
      setAlignmentFile(file);
      setAlignmentData(alignmentObj);

      if (alignmentObj.title && !title) setTitle(alignmentObj.title);
      if (alignmentObj.author && !author) setAuthor(alignmentObj.author);
      if (alignmentObj.language && language === 'pt') setLanguage(alignmentObj.language);

      // Dispara diagnóstico se o documento de texto já estiver carregado
      triggerDiagnosisIfReady(alignmentObj, domItems);
    } catch (err: any) {
      setErrorMessage(err.message || 'Falha ao processar arquivo de alinhamento.');
      setAlignmentFile(null);
      setAlignmentData(null);
    } finally {
      if (alignmentInputRef.current) alignmentInputRef.current.value = '';
    }
  };

  // Handler: Áudio
  const handleAudioUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAudioFile(file);
    setAudioUrlInput('');
    setErrorMessage(null);
    if (!title) {
      setTitle(file.name.replace(/\.[^/.]+$/, '').replace(/_/g, ' '));
    }
    if (audioInputRef.current) audioInputRef.current.value = '';
  };

  // Handler: Livro HTML / TXT / tale_dom.json
  const handleBookHtmlUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMessage(null);
    try {
      const content = await file.text();
      if (!content.trim()) {
        throw new Error('O arquivo de texto/livro selecionado está vazio.');
      }

      const parsedDom = TaleAlignDiagnosticService.parseDomContent(content);
      if (!parsedDom.isValid || !parsedDom.items || parsedDom.items.length === 0) {
        throw new Error(parsedDom.error || 'Não foi possível extrair elementos de texto válidos.');
      }

      setBookHtmlFile(file);
      setDomItems(parsedDom.items);

      if (!title) {
        setTitle(file.name.replace(/\.[^/.]+$/, '').replace(/_/g, ' '));
      }

      // Se temos o alinhamento, executa o diagnóstico completo
      if (alignmentData) {
        triggerDiagnosisIfReady(alignmentData, parsedDom.items);
      } else {
        // Fallback para conteúdo cru se alinhamento ainda não foi carregado
        setBookHtmlContent(content);
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Falha ao processar arquivo de livro.');
      setBookHtmlFile(null);
      setBookHtmlContent('');
      setDomItems(null);
      setDiagnosticReport(null);
    } finally {
      if (bookHtmlInputRef.current) bookHtmlInputRef.current.value = '';
    }
  };

  // Alteração do modelo de offset pelo usuário
  const handleOffsetChange = (newOffset: number) => {
    setSelectedOffset(newOffset);
    if (alignmentData && domItems && domItems.length > 0) {
      const updatedHtml = TaleAlignDiagnosticService.generatePreparedHtml(alignmentData, domItems, newOffset);
      setBookHtmlContent(updatedHtml);
    }
  };

  // Carregar Demonstração / Exemplo Pronto Tale.fyi
  const handleLoadDemo = () => {
    const demoAlignment: TaleFyiNativeAlignment = {
      paras: [
        ["0fp2--yr9v1g", 0.0],
        ["0fp2--yr9v2a", 9.2],
        ["0fp2--yr9v3b", 18.5],
        ["0fp2--yr9v4c", 28.0]
      ],
      phraseBegins: [
        0.0, 3.2, 6.1,       // Parágrafo 1 (3 frases)
        9.2, 13.8,            // Parágrafo 2 (2 frases)
        18.5, 23.1,           // Parágrafo 3 (2 frases)
        28.0, 32.5, 37.0      // Parágrafo 4 (3 frases)
      ],
      phraseIndexes: [
        0, 1, 2,  // Parágrafo 1: frases 0, 1, 2
        0, 1,     // Parágrafo 2: frases 0, 1
        0, 1,     // Parágrafo 3: frases 0, 1
        0, 1, 2   // Parágrafo 4: frases 0, 1, 2
      ],
      title: 'O Pequeno Príncipe (Demonstração)',
      author: 'Antoine de Saint-Exupéry',
      language: 'pt',
      total_duration_seconds: 42.0
    };

    const demoDomItems: TaleDomItem[] = [
      { id: "intro_meta_0", text: "O Pequeno Príncipe — Edição Especial Ilustrada.", tag: "p" },
      { id: "intro_meta_1", text: "Tradução e narração para audiolivro completo.", tag: "p" },
      { id: "0fp2--yr9v1g", text: "Certa vez, quando eu tinha seis anos, vi num livro sobre a Floresta Virgem uma gravura magnífica. Representava uma serpente jiboia engolindo uma fera feroz no meio da mata fechada. O livro dizia que as jiboias engolem a presa inteirinha, sem mastigar nada.", tag: "p" },
      { id: "0fp2--yr9v2a", text: "Depois disso, não conseguem nem se mover e ficam dormindo durante os seis meses da digestão. Pensei muito então sobre as aventuras da selva e fiz meu primeiro desenho a lápis de cor.", tag: "p" },
      { id: "0fp2--yr9v3b", text: "Mostrei minha obra-prima às pessoas grandes e perguntei se o meu desenho lhes dava medo. Elas me responderam surpresas: por que um chapéu daria medo a qualquer pessoa?", tag: "p" },
      { id: "0fp2--yr9v4c", text: "Meu desenho não representava um chapéu, representava uma serpente jiboia digerindo um elefante. Desenhei então o interior da jiboia, para que as pessoas grandes pudessem finalmente compreender. Elas sempre têm necessidade de explicações detalhadas sobre tudo neste mundo.", tag: "p" }
    ];

    setAlignmentData(demoAlignment);
    setDomItems(demoDomItems);
    setTitle('O Pequeno Príncipe (Demo Tale.fyi)');
    setAuthor('Antoine de Saint-Exupéry');
    setLanguage('pt');
    setAudioUrlInput('https://actions.google.com/sounds/v1/ambiences/coffee_shop.ogg');

    triggerDiagnosisIfReady(demoAlignment, demoDomItems);

    setSuccessMessage('Exemplo Tale.fyi carregado com diagnóstico em tempo real!');
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  // Criar e Iniciar Sessão de Teste
  const handleStartTest = async () => {
    if (!alignmentData) {
      setErrorMessage('Por favor, carregue o arquivo de alinhamento nativo (.json ou .txt).');
      return;
    }
    if (!bookHtmlContent.trim() && (!domItems || domItems.length === 0)) {
      setErrorMessage('Por favor, carregue o documento do livro (tale_dom.json ou .html).');
      return;
    }
    if (!audioFile && !audioUrlInput.trim()) {
      setErrorMessage('Por favor, selecione um arquivo de áudio ou forneça uma URL de áudio.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const sessionId = `test_${Date.now()}`;

      // Assegura geração do HTML com o offset selecionado se tivermos os itens
      let finalHtml = bookHtmlContent;
      if (domItems && domItems.length > 0 && alignmentData) {
        finalHtml = TaleAlignDiagnosticService.generatePreparedHtml(alignmentData, domItems, selectedOffset);
      }

      const sessionObj: SavedTestAlignSession = {
        id: sessionId,
        title: title.trim() || alignmentData.title || 'Livro Test Align',
        author: author.trim() || alignmentData.author || 'Autor Teste',
        language: language || alignmentData.language || 'pt',
        htmlContent: finalHtml,
        alignment: alignmentData,
        audioBlob: audioFile || undefined,
        audioSrc: audioUrlInput.trim() || undefined,
        audioFileName: audioFile?.name || (audioUrlInput ? 'Audio Remoto' : 'audio.mp3'),
        createdAt: Date.now(),
        savedTime: 0,
        mappingOffset: selectedOffset,
        diagnosticSummary: diagnosticReport?.summarySummary
      };

      try {
        await testAlignStorage.saveSession(sessionObj);
        await loadSavedSessions();
      } catch (persistErr) {
        console.warn('[TestAlignSection] Aviso na persistência local, abrindo em memória:', persistErr);
      }

      setActiveSession(sessionObj);
    } catch (err: any) {
      setErrorMessage(err.message || 'Falha ao processar sessão de teste.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Abrir Sessão Salva
  const handleOpenSavedSession = async (id: string) => {
    try {
      const session = await testAlignStorage.getSession(id);
      if (session) {
        setActiveSession(session);
      } else {
        alert('Sessão não encontrada no armazenamento local.');
      }
    } catch (err: any) {
      alert(err.message || 'Erro ao carregar sessão.');
    }
  };

  // Excluir Sessão Salva
  const handleDeleteSavedSession = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Deseja realmente remover este teste de alinhamento?')) {
      await testAlignStorage.deleteSession(id);
      await loadSavedSessions();
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Active Player Modal */}
      {activeSession && (
        <TestAlignReaderPlayer
          session={activeSession}
          onClose={() => setActiveSession(null)}
        />
      )}

      {/* Main Banner */}
      <div className="bg-gradient-to-r from-tint-butter/40 via-slate-900 to-slate-900 border border-warn/30 rounded-3xl p-6 md:p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-warn/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-1 bg-warn/20 text-warn border border-warn/40 font-mono text-[10px] font-medium rounded-lg">
                FASE DE TESTE 5
              </span>
              <h2 className="text-xl md:text-2xl font-medium text-white flex items-center gap-2">
                <Sparkles className="text-warn" size={24} /> Test Align — Sincronização Nativa Tale.fyi
              </h2>
            </div>
            <p className="text-xs md:text-sm text-slate-300 font-medium mt-2 max-w-2xl leading-relaxed">
              Carregue <code className="text-warn bg-black/40 px-1.5 py-0.5 rounded font-mono">alinhamento_original.json</code>, <code className="text-warn bg-black/40 px-1.5 py-0.5 rounded font-mono">tale_dom.json</code> e o arquivo <code className="text-warn bg-black/40 px-1.5 py-0.5 rounded font-mono">MP3</code> para auditar a correspondência e rodar o player com os dados 100% intactos.
            </p>
          </div>

          <button
            onClick={handleLoadDemo}
            className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 active:scale-95 text-warn border border-warn/30 rounded-2xl text-[13px] font-medium flex items-center gap-2 transition-all cursor-pointer self-start md:self-auto shrink-0"
          >
            <Sparkles size={16} /> Carregar Demo com Diagnóstico
          </button>
        </div>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="p-4 bg-bad/10 border border-bad/30 rounded-2xl text-bad text-xs font-bold flex items-center gap-2 animate-in fade-in">
          <AlertCircle size={18} className="shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}
      {successMessage && (
        <div className="p-4 bg-ok/10 border border-ok/30 rounded-2xl text-ok text-xs font-bold flex items-center gap-2 animate-in fade-in">
          <Check size={18} className="shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Upload 3-File Box */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 space-y-6">
        <div className="border-b border-slate-800 pb-4">
          <h3 className="text-base font-medium text-white flex items-center gap-2">
            <Upload className="text-warn" size={20} /> Carregar Arquivos Nativos Tale.fyi
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Selecione o arquivo de sincronização (<code className="text-warn">alinhamento_original.json</code>), o arquivo de áudio MP3 e os textos (<code className="text-warn">tale_dom.json</code> ou HTML).
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* 1. Alinhamento Nativo */}
          <div 
            onClick={() => alignmentInputRef.current?.click()}
            className={`p-5 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between group ${
              alignmentData 
                ? 'bg-warn/10 border-warn/40 text-warn' 
                : 'bg-slate-950/60 border-slate-800 hover:border-warn/40 text-slate-400 hover:text-white'
            }`}
          >
            <input 
              type="file" 
              ref={alignmentInputRef}
              onChange={handleAlignmentUpload}
              accept=".json,.txt"
              className="hidden"
            />
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium font-mono px-2 py-0.5 rounded bg-slate-900 border border-slate-800 text-slate-300">
                  ARQUIVO 1 (SINCRONIZAÇÃO)
                </span>
                {alignmentData && <Check size={18} className="text-ok" />}
              </div>
              <h4 className="font-bold text-sm text-white flex items-center gap-2">
                <FileCode size={18} className="text-warn" /> Alinhamento Original
              </h4>
              <p className="text-xs text-slate-400 line-clamp-2">
                {alignmentFile ? alignmentFile.name : (alignmentData ? 'alinhamento_original.json (Carregado)' : 'alinhamento_original.json (.json)')}
              </p>
            </div>

            {alignmentData ? (
              <div className="mt-4 pt-3 border-t border-warn/20 text-[11px] font-mono font-bold text-warn flex items-center justify-between">
                <span>{alignmentData.paras.length} paras</span>
                <span>{alignmentData.phraseBegins.length} frases</span>
              </div>
            ) : (
              <span className="mt-4 text-xs font-bold text-warn/80 group-hover:text-warn flex items-center gap-1">
                <Upload size={14} /> Selecionar alinhamento_original.json
              </span>
            )}
          </div>

          {/* 2. Arquivo de Áudio */}
          <div 
            onClick={() => audioInputRef.current?.click()}
            className={`p-5 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between group ${
              audioFile || audioUrlInput
                ? 'bg-warn/10 border-warn/40 text-warn' 
                : 'bg-slate-950/60 border-slate-800 hover:border-warn/40 text-slate-400 hover:text-white'
            }`}
          >
            <input 
              type="file" 
              ref={audioInputRef}
              onChange={handleAudioUpload}
              accept="audio/*,.mp3,.m4a,.wav,.ogg,.aac"
              className="hidden"
            />
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium font-mono px-2 py-0.5 rounded bg-slate-900 border border-slate-800 text-slate-300">
                  ARQUIVO 2 (ÁUDIO)
                </span>
                {(audioFile || audioUrlInput) && <Check size={18} className="text-ok" />}
              </div>
              <h4 className="font-bold text-sm text-white flex items-center gap-2">
                <Headphones size={18} className="text-warn" /> Áudio Completo (MP3)
              </h4>
              <p className="text-xs text-slate-400 line-clamp-2 truncate">
                {audioFile ? audioFile.name : (audioUrlInput ? 'Áudio URL selecionado' : 'Arquivo de Áudio (.mp3)')}
              </p>
            </div>

            {audioFile ? (
              <div className="mt-4 pt-3 border-t border-warn/20 text-[11px] font-mono font-bold text-warn">
                {(audioFile.size / (1024 * 1024)).toFixed(1)} MB
              </div>
            ) : audioUrlInput ? (
              <div className="mt-4 pt-3 border-t border-warn/20 text-[11px] font-mono font-bold text-warn truncate">
                URL Remota Pronta
              </div>
            ) : (
              <span className="mt-4 text-xs font-bold text-warn/80 group-hover:text-warn flex items-center gap-1">
                <Upload size={14} /> Selecionar Arquivo MP3
              </span>
            )}
          </div>

          {/* 3. Livro tale_dom.json ou HTML */}
          <div 
            onClick={() => bookHtmlInputRef.current?.click()}
            className={`p-5 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between group ${
              domItems || bookHtmlContent
                ? 'bg-warn/10 border-warn/40 text-warn' 
                : 'bg-slate-950/60 border-slate-800 hover:border-warn/40 text-slate-400 hover:text-white'
            }`}
          >
            <input 
              type="file" 
              ref={bookHtmlInputRef}
              onChange={handleBookHtmlUpload}
              accept=".json,.html,.htm,.txt"
              className="hidden"
            />
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium font-mono px-2 py-0.5 rounded bg-slate-900 border border-slate-800 text-slate-300">
                  ARQUIVO 3 (TEXTO DOM)
                </span>
                {(domItems || bookHtmlContent) && <Check size={18} className="text-ok" />}
              </div>
              <h4 className="font-bold text-sm text-white flex items-center gap-2">
                <BookOpen size={18} className="text-warn" /> Textos (tale_dom.json)
              </h4>
              <p className="text-xs text-slate-400 line-clamp-2">
                {bookHtmlFile ? bookHtmlFile.name : (domItems ? `tale_dom.json (${domItems.length} blocos)` : 'tale_dom.json (.json ou .html)')}
              </p>
            </div>

            {domItems ? (
              <div className="mt-4 pt-3 border-t border-warn/20 text-[11px] font-mono font-bold text-warn flex items-center justify-between">
                <span>{domItems.length} blocos de texto</span>
                <span>{(domItems.reduce((acc, i) => acc + i.text.length, 0) / 1000).toFixed(1)}k chars</span>
              </div>
            ) : bookHtmlContent ? (
              <div className="mt-4 pt-3 border-t border-warn/20 text-[11px] font-mono font-bold text-warn">
                {(bookHtmlContent.length / 1024).toFixed(1)} KB carregados
              </div>
            ) : (
              <span className="mt-4 text-xs font-bold text-warn/80 group-hover:text-warn flex items-center gap-1">
                <Upload size={14} /> Selecionar tale_dom.json
              </span>
            )}
          </div>
        </div>

        {/* PAINEL DE DIAGNÓSTICO EM TEMPO REAL (FASE 1) */}
        {diagnosticReport && (
          <div className="mt-6 border border-warn/30 bg-slate-950/80 rounded-3xl p-5 md:p-7 space-y-6 animate-in fade-in duration-300">
            {/* Header do Diagnóstico */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-warn/10 border border-warn/30 text-warn">
                  <Activity size={22} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white flex items-center gap-2">
                    Diagnóstico de Alinhamento e Auditoria de Segmentação
                  </h3>
                  <p className="text-xs text-slate-400">
                    Auditoria matemática e acústica entre alinhamento_original.json e tale_dom.json
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsDiagnosticOpen(!isDiagnosticOpen)}
                className="px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-xs text-slate-300 font-bold border border-slate-800 flex items-center gap-1.5 transition-colors self-start sm:self-auto"
              >
                {isDiagnosticOpen ? (
                  <><span>Ocultar Detalhes</span><ChevronUp size={16} /></>
                ) : (
                  <><span>Ver Diagnóstico Completo</span><ChevronDown size={16} /></>
                )}
              </button>
            </div>

            {/* 4 Cards de Métricas Principais */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Card 1: Alinhamento */}
              <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-4 space-y-1">
                <span className="text-[10px] font-mono font-normal uppercase tracking-[0.14em] text-slate-400 block">
                  1. Alinhamento Original
                </span>
                <p className="text-lg font-semibold text-white">
                  {diagnosticReport.alignmentStats.parasCount} paras
                </p>
                <p className="text-[11px] text-slate-400 font-mono">
                  {diagnosticReport.alignmentStats.phraseBeginsCount} frases • {diagnosticReport.alignmentStats.zeroCount} zeros
                </p>
                <div className="pt-1 flex items-center gap-1 text-[10px] text-ok font-bold">
                  <CheckCircle2 size={12} /> Zeros = Paras ({diagnosticReport.alignmentStats.zeroMatchesParas ? '100% Coerente' : 'Inconsistente'})
                </div>
              </div>

              {/* Card 2: tale_dom */}
              <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-4 space-y-1">
                <span className="text-[10px] font-mono font-normal uppercase tracking-[0.14em] text-slate-400 block">
                  2. Documento tale_dom
                </span>
                <p className="text-lg font-semibold text-white">
                  {diagnosticReport.domStats.totalItems} blocos
                </p>
                <p className="text-[11px] text-slate-400 font-mono">
                  {(diagnosticReport.domStats.totalCharacters / 1000).toFixed(1)}k chars • ~{diagnosticReport.domStats.totalWords} palavras
                </p>
                <div className="pt-1 text-[10px] text-slate-400">
                  Média: {Math.round(diagnosticReport.domStats.avgCharsPerItem)} chars/bloco
                </div>
              </div>

              {/* Card 3: Auditoria de IDs */}
              <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-4 space-y-1">
                <span className="text-[10px] font-mono font-normal uppercase tracking-[0.14em] text-slate-400 block">
                  3. Nomenclatura de IDs
                </span>
                <p className="text-lg font-semibold text-warn">
                  {diagnosticReport.idAnalysis.matchingIdsCount > 0 ? `${diagnosticReport.idAnalysis.matchingIdsCount} em comum` : 'IDs Distintos'}
                </p>
                <p className="text-[11px] text-slate-400 font-mono truncate" title={`paras: ${diagnosticReport.idAnalysis.parasIdSample[0]} | dom: ${diagnosticReport.idAnalysis.domIdSample[0]}`}>
                  {diagnosticReport.idAnalysis.parasIdSample[0]} vs {diagnosticReport.idAnalysis.domIdSample[0]}
                </p>
                <div className="pt-1 text-[10px] text-warn/90 font-medium">
                  {diagnosticReport.idAnalysis.hasDirectIdMatch ? 'Vínculo por ID direto' : 'Vínculo Estrutural/Narrativo'}
                </div>
              </div>

              {/* Card 4: Taxa de Fala */}
              <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-4 space-y-1">
                <span className="text-[10px] font-mono font-normal uppercase tracking-[0.14em] text-slate-400 block">
                  4. Taxa de Fala Estimada
                </span>
                <p className="text-lg font-semibold text-ok">
                  ~{(diagnosticReport.segmentationAnalysis.models.find(m => m.offset === selectedOffset)?.avgCharsPerSec || 14).toFixed(1)} ch/s
                </p>
                <p className="text-[11px] text-slate-400 font-mono">
                  {((diagnosticReport.segmentationAnalysis.models.find(m => m.offset === selectedOffset)?.avgCharsPerSec || 14) * 60 / 5.5).toFixed(0)} palavras/min
                </p>
                <div className="pt-1 text-[10px] text-ok font-bold flex items-center gap-1">
                  <CheckCircle2 size={12} /> Ritmo Humano Plausível
                </div>
              </div>
            </div>

            {isDiagnosticOpen && (
              <div className="space-y-6 pt-2">
                {/* Investigação de Segmentação & Explicação do Descompasso Inicial */}
                <div className="p-4 bg-warn/10 border border-warn/30 rounded-2xl space-y-2">
                  <div className="flex items-center gap-2 text-warn font-normal text-xs uppercase font-mono tracking-[0.14em]">
                    <Info size={16} /> Investigação de Segmentação no Início do Áudio
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    <strong>Por que o primeiro bloco de texto possui 942 caracteres enquanto paras[0] tem apenas 7.2s?</strong><br />
                    Uma taxa de 942 caracteres em 7.2 segundos equivaleria a <strong>130.8 caracteres/segundo</strong> (velocidade impossível para narração humana).
                    Isso comprova que os dois primeiros blocos de <code className="text-warn font-mono">tale_dom.json</code> correspondem a <strong>metadados e apresentação do livro</strong> que não possuem áudio associado em <code className="text-warn font-mono">paras</code>.
                    Ao adotar o <strong>Modelo Narrativo (Offset +2)</strong>, o 1º parágrafo da narrativa (bloco 2) casa perfeitamente com <code className="text-warn font-mono">paras[0]</code>, estabilizando a velocidade de fala em <strong>13.8 caracteres/segundo</strong> (taxa de audiolivro humano padrão).
                  </p>
                </div>

                {/* Seletor de Modelo de Mapeamento (Fase 2) */}
                <div className="space-y-2">
                  <label className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-slate-300 flex items-center gap-2">
                    <Sliders size={16} className="text-warn" /> Escolha o Modelo de Correspondência para o Teste:
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {diagnosticReport.segmentationAnalysis.models.map((model) => (
                      <div
                        key={model.offset}
                        onClick={() => handleOffsetChange(model.offset)}
                        className={`p-4 rounded-2xl border transition-all cursor-pointer space-y-2 ${
                          selectedOffset === model.offset
                            ? 'bg-warn/15 border-warn text-white'
                            : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:text-white hover:border-slate-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${
                            selectedOffset === model.offset ? 'bg-warn text-ink' : 'bg-slate-800 text-slate-400'
                          }`}>
                            OFFSET {model.offset > 0 ? `+${model.offset}` : model.offset}
                          </span>
                          {model.isRecommended && (
                            <span className="text-[10px] font-bold text-warn flex items-center gap-1">
                              <Sparkles size={12} /> RECOMENDADO
                            </span>
                          )}
                        </div>
                        <h4 className="font-bold text-xs text-white leading-snug">{model.name}</h4>
                        <p className="text-[11px] text-slate-400 leading-relaxed line-clamp-3">
                          {model.description}
                        </p>
                        <div className="pt-2 border-t border-white/5 flex items-center justify-between text-[11px] font-mono">
                          <span>Taxa Média:</span>
                          <span className={model.avgCharsPerSec >= 9 && model.avgCharsPerSec <= 25 ? 'text-ok font-bold' : 'text-bad font-bold'}>
                            {model.avgCharsPerSec.toFixed(1)} ch/s
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tabela de Amostragem Lado a Lado (Início, Meio e Fim) */}
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/10 pb-2">
                    <h4 className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-slate-300 flex items-center gap-2">
                      <ListFilter size={16} className="text-warn" /> Amostragem de Correspondência Real (Lado a Lado)
                    </h4>

                    {/* Abas de Amostragem */}
                    <div className="flex items-center gap-1.5 bg-slate-900 p-1 rounded-xl border border-slate-800">
                      <button
                        onClick={() => setActiveSampleTab('beginning')}
                        className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                          activeSampleTab === 'beginning' ? 'bg-warn text-ink' : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        Início (Blocos 0–4)
                      </button>
                      <button
                        onClick={() => setActiveSampleTab('middle')}
                        className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                          activeSampleTab === 'middle' ? 'bg-warn text-ink' : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        Meio (Central)
                      </button>
                      <button
                        onClick={() => setActiveSampleTab('end')}
                        className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                          activeSampleTab === 'end' ? 'bg-warn text-ink' : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        Fim (Últimos 5)
                      </button>
                    </div>
                  </div>

                  {/* Tabela */}
                  <div className="overflow-x-auto rounded-2xl border border-slate-800">
                    <table className="w-full text-left text-xs font-sans">
                      <thead className="bg-slate-900 text-slate-400 uppercase font-mono text-[10px] tracking-[0.14em] border-b border-slate-800">
                        <tr>
                          <th className="p-3">DOM (tale_dom)</th>
                          <th className="p-3">Texto Prévia</th>
                          <th className="p-3">Chars</th>
                          <th className="p-3">Paras (Áudio)</th>
                          <th className="p-3">Início</th>
                          <th className="p-3">Duração</th>
                          <th className="p-3">Taxa Fala</th>
                          <th className="p-3">Classificação</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/60 bg-slate-950/40 font-mono text-[11px]">
                        {diagnosticReport.samples[activeSampleTab].map((row, idx) => (
                          <tr key={idx} className="hover:bg-slate-900/40 transition-colors">
                            <td className="p-3 text-slate-300 font-bold">
                              #{row.domIndex} <span className="text-slate-500 text-[10px]">({row.domId})</span>
                            </td>
                            <td className="p-3 text-slate-300 font-sans max-w-xs truncate" title={row.domPreview}>
                              {row.domPreview}
                            </td>
                            <td className="p-3 text-slate-400">{row.domLength}</td>
                            <td className="p-3 text-warn font-bold">
                              {row.paraIndex !== undefined ? `#${row.paraIndex} (${row.paraId})` : '—'}
                            </td>
                            <td className="p-3 text-slate-300">
                              {row.startTime !== undefined ? `${row.startTime.toFixed(1)}s` : '—'}
                            </td>
                            <td className="p-3 text-slate-400">
                              {row.duration !== undefined ? `${row.duration.toFixed(1)}s` : '—'}
                            </td>
                            <td className="p-3 font-bold">
                              {row.charsPerSec ? (
                                <span className={row.charsPerSec >= 8 && row.charsPerSec <= 26 ? 'text-ok' : 'text-warn'}>
                                  {row.charsPerSec.toFixed(1)} ch/s
                                </span>
                              ) : '—'}
                            </td>
                            <td className="p-3">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                row.status === 'narrativo'
                                  ? 'bg-ok/10 text-ok border border-ok/30'
                                  : row.status === 'metadados'
                                  ? 'bg-warn/10 text-warn border border-warn/30'
                                  : row.status === 'indice'
                                  ? 'bg-tintd-lavender/10 text-tintd-lavender border border-tintd-lavender/30'
                                  : 'bg-slate-800 text-slate-400'
                              }`}>
                                {row.statusLabel}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Tratamento de Blocos Não-Narrativos */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div className="p-3.5 bg-slate-900/60 border border-slate-800 rounded-2xl space-y-1.5">
                    <span className="text-[10px] font-mono uppercase tracking-[0.14em] font-normal text-warn flex items-center gap-1.5">
                      <Layers size={14} /> Elementos de Apresentação (Iniciais):
                    </span>
                    <p className="text-[11px] text-slate-300 leading-relaxed">
                      Os blocos iniciais detectados (itens 0 e 1 de tale_dom) serão renderizados como blocos neutros de metadados, <strong>sem receber destaque indevido de narração</strong> quando o áudio começar a narrar a história.
                    </p>
                  </div>

                  <div className="p-3.5 bg-slate-900/60 border border-slate-800 rounded-2xl space-y-1.5">
                    <span className="text-[10px] font-mono uppercase tracking-[0.14em] font-normal text-tintd-lavender flex items-center gap-1.5">
                      <Layers size={14} /> Elementos de Fechamento (Finais):
                    </span>
                    <p className="text-[11px] text-slate-300 leading-relaxed">
                      Os blocos finais (Capítulos e Índice) mapeiam com exatidão para <code className="text-tintd-lavender font-mono">paras[272]</code> e <code className="text-tintd-lavender font-mono">paras[273]</code>. Ao término do livro, o player não salta para o parágrafo 0.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Optional Metadata Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
          <div>
            <label className="text-[11px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] block mb-1">Título</label>
            <input 
              type="text" 
              value={title} 
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Livro Tale.fyi Sincronizado"
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-white outline-none focus:border-warn"
            />
          </div>
          <div>
            <label className="text-[11px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] block mb-1">Autor</label>
            <input 
              type="text" 
              value={author} 
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="Ex: Autor Original"
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-white outline-none focus:border-warn"
            />
          </div>
          <div>
            <label className="text-[11px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] block mb-1">Idioma</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-white outline-none focus:border-warn"
            >
              <option value="pt">Português (pt)</option>
              <option value="en">Inglês (en)</option>
              <option value="fr">Francês (fr)</option>
              <option value="es">Espanhol (es)</option>
              <option value="de">Alemão (de)</option>
              <option value="it">Italiano (it)</option>
            </select>
          </div>
        </div>

        {/* Start Player Action */}
        <button
          onClick={handleStartTest}
          disabled={!alignmentData || (!bookHtmlContent && (!domItems || domItems.length === 0)) || (!audioFile && !audioUrlInput) || isProcessing}
          className="text-[13px] font-medium w-full h-12 bg-warn hover:bg-warn disabled:opacity-40 disabled:cursor-not-allowed text-ink rounded-full flex items-center justify-center gap-2 transition-all active:scale-98 cursor-pointer"
        >
          {isProcessing ? (
            <>
              <Loader2 size={18} className="animate-spin" /> Processando Alinhamento...
            </>
          ) : (
            <>
              <Play size={18} fill="currentColor" /> Iniciar Player Test Align (Tale.fyi Sync)
            </>
          )}
        </button>
      </div>

      {/* History of Saved Test Sessions */}
      {sessions.length > 0 && (
        <div className="space-y-4 pt-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-slate-400 flex items-center gap-2">
              <BookOpen className="text-warn" size={18} /> Testes de Alinhamento Salvos ({sessions.length})
            </h3>
            <button
              onClick={loadSavedSessions}
              className="text-xs text-slate-500 hover:text-warn flex items-center gap-1 transition-colors"
            >
              <RefreshCw size={12} /> Atualizar
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sessions.map((item) => (
              <div
                key={item.id}
                onClick={() => handleOpenSavedSession(item.id)}
                className="p-5 bg-slate-900 hover:bg-slate-850 border border-slate-800 hover:border-warn/40 rounded-3xl transition-all cursor-pointer flex items-center justify-between group"
              >
                <div className="flex items-center gap-4 overflow-hidden">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-warn to-tint-lavender flex items-center justify-center text-white text-xl font-semibold shrink-0 border border-white/10">
                    📖
                  </div>
                  <div className="truncate">
                    <h4 className="font-semibold text-sm text-white group-hover:text-warn transition-colors truncate">
                      {item.title}
                    </h4>
                    <p className="text-xs text-slate-400 font-medium truncate mt-0.5">{item.author}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-2 py-0.5 bg-warn/10 text-warn font-mono text-[10px] font-medium rounded-md border border-warn/20">
                        {item.language}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono">
                        {item.parasCount} paras • {item.phrasesCount} frases
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={(e) => handleDeleteSavedSession(item.id, e)}
                    className="p-3 text-slate-500 hover:text-bad hover:bg-tint-rose/30 rounded-2xl transition-colors"
                    title="Excluir Teste"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
